import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AdminAuthProvider, useAdminAuth } from '@/contexts/AdminAuthContext';
import { useState, useEffect } from 'react';
import HomePage from "@/pages/HomePage";
import AuthPage from "@/pages/AuthPage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import SubscribePage from "@/pages/SubscribePage";
import VerifyPage from "@/pages/VerifyPage";
import VerifyDomainPage from "@/pages/VerifyDomainPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminShell from "@/pages/admin/AdminShell";
import PricingFlowPage from "@/pages/PricingFlowPage";
import LearnPage from "@/pages/LearnPage";
import DefendPage from "@/pages/DefendPage";
import LaunchPage from "@/pages/LaunchPage";
import ReachPage from "@/pages/ReachPage";
import PrivacyPage from "@/pages/PrivacyPage";
import CookiesPage from "@/pages/CookiesPage";
import TermsPage from "@/pages/TermsPage";
import GdprPage from "@/pages/GdprPage";
import BlogPage from "@/pages/BlogPage";
import AcodiaWatchPage from "@/pages/AcodiaWatchPage";
import SettingsPage from "@/pages/SettingsPage";
import ContactPage from "@/pages/ContactPage";

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

// Clear any stale homepage-scanner state left in a returning visitor's browser.
// The public scanner (and its GlobalScanBanner) were removed 2026-05-28; this
// stops the old banner from re-appearing after the redeploy. Safe one-time no-op.
try { localStorage.removeItem('acodia_scan_state'); } catch (e) { /* localStorage unavailable */ }

function ProtectedRoute({ children, requireSubscription = false, requireDomain = false }) {
  const { user, loading, tokens, authFetch } = useAuth();
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [domains, setDomains] = useState([]);

  useEffect(() => {
    async function checkAccess() {
      if (!tokens || !user) {
        setCheckingAccess(false);
        return;
      }

      if (requireSubscription || requireDomain) {
        try {
          // Check subscription status from database
          const subRes = await authFetch(`${BACKEND_URL}/api/billing/subscription`);
          if (subRes.ok) {
            const subData = await subRes.json();
            setSubscription(subData);
          }

          // Check verified domains
          if (requireDomain) {
            const domRes = await authFetch(`${BACKEND_URL}/api/verify/domains`);
            if (domRes.ok) {
              const domData = await domRes.json();
              setDomains(domData.domains || []);
            }
          }
        } catch (err) {
          console.error('Access check failed:', err);
        }
      }
      
      setCheckingAccess(false);
    }

    checkAccess();
  }, [tokens, user, requireSubscription, requireDomain]);

  // Loading state
  if (loading || checkingAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="font-mono text-sm" style={{ color: 'var(--a)' }}>Loading...</div>
      </div>
    );
  }

  // Check 1: Authentication — unauthenticated visitors go through /auth, not
  // the old /login route which has been folded into the unified flow.
  if (!tokens || !user) {
    return <Navigate to="/auth" replace />;
  }

  // Check 2: Subscription gate. Trial users always pass — we only block
  // visitors whose subscription has lapsed or has never been created. We
  // render an inline screen with navigation rather than a `Navigate` to a
  // potentially-dead-end page, so the visitor can always get home.
  if (requireSubscription) {
    const status = subscription?.status;
    // Mirrors the backend /scan/active allowlist. "trialing" is intentionally
    // omitted — no code path writes it; trial subscriptions use "trial".
    const allowed = ['active', 'trial', 'cancelling'].includes(status);
    if (status && !allowed) {
      return (
        <div
          data-testid="subscription-blocked"
          style={{
            minHeight: '100vh',
            background: 'var(--bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '1rem',
            padding: '2rem',
          }}
        >
          <p style={{ color: 'var(--white)', fontFamily: 'DM Sans, sans-serif', fontSize: '1rem' }}>
            Your subscription is not active.
          </p>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <a
              href="/watch"
              style={{ color: 'var(--a)', fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem' }}
            >
              View plans →
            </a>
            <a
              href="/"
              style={{ color: 'var(--ts)', fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem' }}
            >
              Go to homepage
            </a>
          </div>
        </div>
      );
    }
  }

  // Check 3: Verified domain (if required)
  if (requireDomain) {
    const hasVerifiedDomain = domains.some(d => d.verified);
    if (!hasVerifiedDomain) {
      return <Navigate to="/verify" replace />;
    }
  }

  return children;
}

function AdminProtectedRoute({ children }) {
  const { admin, token, loading } = useAdminAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="font-mono text-sm" style={{ color: 'var(--a)' }}>Loading…</div>
      </div>
    );
  }
  if (!admin || !token) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppRoutes() {
  const { user, tokens } = useAuth();
  return (
    <>
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={
        tokens && user ? <Navigate to="/dashboard" replace /> : <LoginPage />
      } />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/subscribe" element={
        <ProtectedRoute requireSubscription={false} requireDomain={false}>
          <SubscribePage />
        </ProtectedRoute>
      } />
      <Route path="/verify" element={
        <ProtectedRoute requireSubscription={true} requireDomain={false}>
          <VerifyPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute requireSubscription={true} requireDomain={false}>
          <DashboardPage section="overview" />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/domains" element={
        <ProtectedRoute requireSubscription={true} requireDomain={false}>
          <DashboardPage section="domains" />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/domains/verify/:domainId" element={
        <ProtectedRoute requireSubscription={true} requireDomain={false}>
          <VerifyDomainPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/billing" element={
        <ProtectedRoute requireSubscription={true} requireDomain={false}>
          <DashboardPage section="billing" />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/scan-history" element={
        <ProtectedRoute requireSubscription={true} requireDomain={false}>
          <DashboardPage section="scan-history" />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/alerts" element={
        <ProtectedRoute requireSubscription={true} requireDomain={false}>
          <DashboardPage section="alerts" />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/settings" element={
        <ProtectedRoute requireSubscription={true} requireDomain={false}>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/billing/success" element={
        <ProtectedRoute requireSubscription={true} requireDomain={false}>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/billing/cancel" element={
        <ProtectedRoute requireSubscription={true} requireDomain={false}>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/pricing" element={<PricingFlowPage />} />
      <Route path="/learn" element={<LearnPage />} />
<Route path="/defend" element={<DefendPage />} />
<Route path="/launch" element={<LaunchPage />} />
<Route path="/reach" element={<ReachPage />} />
<Route path="/privacy" element={<PrivacyPage />} />
<Route path="/cookies" element={<CookiesPage />} />
<Route path="/terms" element={<TermsPage />} />
<Route path="/gdpr" element={<GdprPage />} />
<Route path="/blog" element={<BlogPage />} />
<Route path="/contact" element={<ContactPage />} />
<Route path="/watch" element={<AcodiaWatchPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={
        <AdminProtectedRoute>
          <AdminShell />
        </AdminProtectedRoute>
      } />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminAuthProvider>
          <AppRoutes />
        </AdminAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
